import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  getGetNotesQueryKey,
  useCreateNote,
  useGetNotes,
  useImproveNote,
  useUpdateNote,
} from "~/api/notes/notes";
import {
  NOTE_IMPROVEMENT_PROMPT,
  SUMMARY_CONTEXT_LINE,
  TEXT_MARKER,
} from "~/constants/prompts";
import { useChatDocumentParams } from "~/helpers";
import logger from "~/utils/logger";
import {
  hasBase64Images,
  replaceBase64ImagesWithCdn,
} from "../utils/noteImageUpload";

export const useNote = () => {
  const { organizationId, caseId } = useChatDocumentParams();
  const queryClient = useQueryClient();

  const {
    data: notesData,
    isFetching,
    isPending,
  } = useGetNotes(organizationId, caseId, {
    query: {
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      notifyOnChangeProps: ["data"],
    },
  });

  const { mutateAsync: createNote } = useCreateNote();
  const { mutateAsync: updateNote } = useUpdateNote();
  const { mutateAsync: improveNote } = useImproveNote();

  const note = notesData?.notes?.[0];
  const noteContent = note?.content ?? "";
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingImprovement, setIsProcessingImprovement] = useState(false);
  const debouncedSaveRef = useRef<((content: string) => Promise<void>) | null>(
    null
  );
  // Processing flag to prevent concurrent improvements
  const isProcessingQueueRef = useRef(false);

  if (!debouncedSaveRef.current) {
    debouncedSaveRef.current = debounce(async (content: string) => {
      // Replace any remaining base64 images with CDN URLs before saving
      let processedContent = content;
      if (hasBase64Images(content)) {
        try {
          processedContent = await replaceBase64ImagesWithCdn(
            content,
            organizationId,
            caseId
          );
        } catch (error) {
          logger.error("Failed to process base64 images:", error);
          // Continue with original content if image processing fails
        }
      }

      const currentNote = queryClient.getQueryData(
        getGetNotesQueryKey(organizationId, caseId)
      ) as any;
      const noteToUpdate = currentNote?.notes?.[0];

      if (!noteToUpdate || noteToUpdate.id === "temp") {
        try {
          setIsSaving(true);
          const newNote = await createNote({
            organizationId,
            projectId: caseId,
            data: { content: processedContent },
          });
          queryClient.setQueryData(
            getGetNotesQueryKey(organizationId, caseId),
            (oldData: any) => ({
              ...oldData,
              notes: [newNote],
            })
          );
        } catch (error) {
          logger.error("Failed to create note:", error);
        } finally {
          setIsSaving(false);
        }
      } else {
        try {
          setIsSaving(true);
          const updatedNote = await updateNote({
            organizationId,
            projectId: caseId,
            noteId: noteToUpdate.id,
            data: { content: processedContent },
          });
          //Updating cached data with the new note
          queryClient.setQueryData(
            getGetNotesQueryKey(organizationId, caseId),
            (oldData: any) => ({
              ...oldData,
              notes: [updatedNote],
            })
          );
        } catch (error) {
          logger.error("Failed to update note:", error);
        } finally {
          setIsSaving(false);
        }
      }
    }, 3000);
  }

  const handleAutoImproveChunk = async (transcribedText: string) => {
    if (!transcribedText?.trim()) return;

    if (isProcessingQueueRef.current || isSaving) return;

    isProcessingQueueRef.current = true;

    try {
      const currentNote = queryClient.getQueryData(
        getGetNotesQueryKey(organizationId, caseId)
      ) as any;
      const actualCurrentText = currentNote?.notes?.[0]?.content;

      const combinedText = actualCurrentText
        ? `${actualCurrentText} ${transcribedText}`
        : transcribedText;

      const improvedResult = await improveNote({
        data: {
          improvementPrompt: `${NOTE_IMPROVEMENT_PROMPT}${TEXT_MARKER}"${combinedText}"`,
          type: "gemini",
          model: "gemini-2.0-flash-lite",
        },
      });

      return improvedResult.improvedNote;
    } catch (err) {
      logger.error("Auto improvement failed:", err);
    } finally {
      isProcessingQueueRef.current = false;
    }
  };

  const handleNoteImprove = async (summaryText: string) => {
    setIsProcessingImprovement(true);
    const currentNote = queryClient.getQueryData(
      getGetNotesQueryKey(organizationId, caseId)
    ) as any;
    const actualCurrentText = currentNote?.notes?.[0]?.content;
    try {
      const improvedResult = await improveNote({
        data: {
          improvementPrompt: `${NOTE_IMPROVEMENT_PROMPT}${summaryText ? `\n${SUMMARY_CONTEXT_LINE}${summaryText}` : ""}${TEXT_MARKER}${actualCurrentText}"`,
          type: "gemini",
          model: "gemini-2.0-flash-lite",
        },
      });
      await handleContentChange(improvedResult.improvedNote);
    } catch (err) {
      logger.error("Full note improvement failed:", err);
    } finally {
      setIsProcessingImprovement(false);
    }
  };

  const handleContentChange = async (content: string) => {
    const currentNote = queryClient.getQueryData(
      getGetNotesQueryKey(organizationId, caseId)
    ) as any;
    const currentContent = currentNote?.notes?.[0]?.content ?? "";

    const normalizedCurrent = currentContent.trim();
    const normalizedNew = content.trim();

    const isCurrentEmpty =
      !normalizedCurrent ||
      normalizedCurrent === "<p></p>" ||
      normalizedCurrent === "<br>" ||
      normalizedCurrent === "<p><br></p>";
    const isNewEmpty =
      !normalizedNew ||
      normalizedNew === "<p></p>" ||
      normalizedNew === "<br>" ||
      normalizedNew === "<p><br></p>";

    if (isCurrentEmpty && isNewEmpty) {
      return;
    }

    if (normalizedCurrent === normalizedNew) {
      return;
    }

    const currentTime = new Date().toISOString();

    queryClient.setQueryData(
      getGetNotesQueryKey(organizationId, caseId),
      (oldData: any) => {
        if (!oldData?.notes?.[0]) {
          return {
            ...oldData,
            notes: [
              { id: "temp", content, author: "", updatedAt: currentTime },
            ],
          };
        }
        const newNotes = [...oldData.notes];
        newNotes[0] = { ...newNotes[0], content, updatedAt: currentTime };
        return { ...oldData, notes: newNotes };
      }
    );
    if (debouncedSaveRef.current) {
      await debouncedSaveRef.current(content);
    }
  };

  return {
    note,
    noteContent,
    isFetching: isFetching || isPending,
    isSaving: isSaving,
    isProcessingImprovement: isProcessingImprovement,
    isProcessingQueue: isProcessingQueueRef.current,
    handleContentChange,
    handleAutoImproveChunk,
    handleNoteImprove,
  };
};

function debounce<F extends (...args: any[]) => Promise<any>>(
  func: F,
  waitFor: number
) {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<F>): Promise<void> => {
    return new Promise((resolve) => {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(async () => {
        await func(...args);
        resolve();
      }, waitFor);
    });
  };
}
